import React, { useState, useCallback, useEffect, useRef } from 'react';
import Button from '../../BaseComponents/Button';
import QuotesTable from './QuotesTable/index';
import QuotesModal from './QuotesModal';
import ViewQuoteModal from './QuotesModal/ViewQuoteModal';
import DeleteQuoteModal from './QuotesModal/DeleteQuoteModal';
import './QuotesTab.scss';

import { Quote, getQuotesList } from '../../../api/quotes';

const LIMIT = 10;

const QuotesTab = () => {
    const [quotesList, setQuotesList] = useState<Quote[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isFetchingRef = useRef(false);

    const fetchQuotes = useCallback(async (currentOffset: number, isInitial = false) => {
        if (isFetchingRef.current) return;
        
        isFetchingRef.current = true;
        setIsFetching(true);
        if (isInitial) setIsLoading(true);

        try {
            const { quotesList: fetchedQuotes, total_count } = await getQuotesList(LIMIT, currentOffset);
            
            if (isInitial) {
                setQuotesList(fetchedQuotes);
            } else {
                setQuotesList(prev => [...prev, ...fetchedQuotes]);
            }
            
            setTotalCount(total_count);
            setHasMore(currentOffset + LIMIT < total_count);
            setOffset(currentOffset + LIMIT);
        } catch (error) {
            console.error('Error fetching quotes:', error);
        } finally {
            isFetchingRef.current = false;
            setIsFetching(false);
            if (isInitial) setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQuotes(0, true);
    }, [fetchQuotes]);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [quoteToEdit, setQuoteToEdit] = useState<Quote | null>(null);
    const [quoteToView, setQuoteToView] = useState<Quote | null>(null);
    const [quoteToDelete, setQuoteToDelete] = useState<Quote | null>(null);

    const onAddQuoteBtnClick = () => {
        setQuoteToEdit(null);
        setIsFormModalOpen(true);
    };

    const handleView = (quote: Quote) => {
        setQuoteToView(quote);
    };

    const handleEdit = (quote: Quote) => {
        setQuoteToView(null);
        setQuoteToEdit(quote);
        setIsFormModalOpen(true);
    };

    const handleDelete = (quote: Quote) => {
        setQuoteToDelete(quote);
    };

    const confirmDeleteQuote = async (quote: Quote) => {
        // Refetch entire list after successful deletion
        await fetchQuotes(0, true);
        setQuoteToDelete(null);
        setQuoteToView(null);
    };

    const onQuoteSaved = (data: any) => {
        // Refetch from the start to get the most up-to-date list after update or create
        fetchQuotes(0, true);
        setIsFormModalOpen(false);
        setQuoteToEdit(null);
    };

    return (
        <>
            <div className='search-row-container'>
                <div className='search-and-filter-container'>
                    <div className='count-container'>
                        {totalCount === 1 ? 'Quote ( ' + totalCount + ' )' : 'Quotes ( ' + totalCount + ' )'}
                    </div>
                </div>
                <Button
                    isSecondary={true}
                    text='Add Quote'
                    onClick={onAddQuoteBtnClick}
                />
            </div>

            <div className='panels-container'>
                {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading quotes...</div>
                ) : (
                    <QuotesTable
                        quotesList={quotesList}
                        hasMore={hasMore}
                        isLoadingMore={isFetching && !isLoading}
                        onLoadMore={() => fetchQuotes(offset)}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                )}
            </div>

            {isFormModalOpen && (
                <QuotesModal
                    isOpen={isFormModalOpen}
                    onClose={() => {
                        setIsFormModalOpen(false);
                        setQuoteToEdit(null);
                    }}
                    onSaved={onQuoteSaved}
                    quoteToEdit={quoteToEdit}
                />
            )}

            {!!quoteToView && (
                <ViewQuoteModal
                    isOpen={!!quoteToView}
                    onClose={() => setQuoteToView(null)}
                    quote={quoteToView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            )}

            {!!quoteToDelete && (
                <DeleteQuoteModal
                    isOpen={!!quoteToDelete}
                    onClose={() => setQuoteToDelete(null)}
                    quoteToDelete={quoteToDelete}
                    onQuoteDeleted={confirmDeleteQuote}
                />
            )}
        </>
    );
};

export default QuotesTab;
